import { Component, computed, EventEmitter, inject, OnInit, Output, Signal, signal, ViewEncapsulation, WritableSignal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import { BodyComponent } from '@shared/components/body/body.component';
import { BoxComponent } from '@shared/components/box/box.component';
import { EmptyDataBlockComponent } from '@shared/components/empty-data-block/empty-data-block.component';
import { FooterComponent } from '@shared/components/footer/footer.component';
import { SelectComponent } from '@shared/components/form/select/select.component';
import { TextboxComponent } from '@shared/components/form/textbox/textbox.component';
import { HeaderComponent } from '@shared/components/header/header.component';
import { SubtitleBlockComponent } from '@shared/components/subtitle-block/subtitle-block.component';
import { inventories } from '@vessel-reports/noon/noon.constants';
import { NoonActions } from '@vessel-reports/noon/state/noon.actions';
import { NoonState } from '@vessel-reports/noon/state/noon.state';
import { InventoryType, NoonReportDetail } from '@vessel-reports/noon/state/noon.types';
import { MathRoundPipe } from 'app/core/pipe/math-round.pipe';
import { PopupService } from 'app/core/services/popup.service';
import { some } from 'lodash';
import cloneDeep from 'lodash-es/cloneDeep';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'supply-details',
  imports: [
    TranslocoPipe,
    BoxComponent,
    HeaderComponent,
    BodyComponent,
    SelectComponent,
    TextboxComponent,
    FooterComponent,
    SubtitleBlockComponent,
    EmptyDataBlockComponent,
    ReactiveFormsModule,
    MathRoundPipe,
    MatIcon
  ],
  templateUrl: './supply-details.component.html',
  styleUrl: './supply-details.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class SupplyDetailsComponent implements OnInit {
  protected readonly translocoService: TranslocoService = inject(TranslocoService);

  type: WritableSignal<'view' | 'add' | 'edit'> = signal('view');
  protected readonly inventories: InventoryType[] = inventories;
  @Output() back: EventEmitter<void> = new EventEmitter<void>();
  @Output() save: EventEmitter<any> = new EventEmitter<any>();
  form: UntypedFormGroup;
  private readonly _noonReportActions: NoonActions = inject(NoonActions);
  private readonly _noonReportState: NoonState = inject(NoonState);
  detailNoonReport: Signal<NoonReportDetail> = computed(() => this._noonReportState.detailNoonReport());
  supplies: Signal<any> = computed(() => this._noonReportState.detailNoonReport().supplies);
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  private _popupService: PopupService = inject(PopupService);

  constructor(private _formBuilder: FormBuilder) {}

  ngOnInit() {
    this.form = this._formBuilder.group({
      id: [''],
      vessel_report_id: [this.detailNoonReport().id],
      inventory_item_id: ['', Validators.required],
      quantity: ['', Validators.required]
    });
  }

  get headerTitle() {
    return this.type() === 'add'
      ? 'ship_interface.add_supply_details'
      : this.type() === 'view'
        ? 'ship_interface.enter_inventory_supply'
        : 'ship_interface.edit_supply_details';
  }

  onBack: FunctionType = () => {
    if (this.type() !== 'view') {
      this.type.set('view');
    } else {
      this.back.emit();
    }
  };

  onDelete: FunctionType = () => {
    this._noonReportActions.actions.deleteInventorySupply({ id: this.form.get('id').value });
    this._noonReportActions.actions.deleteInventorySupplySuccess$.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
      this.form.get('inventory_item_id').reset();
      this.form.get('quantity').reset();
      this.form.get('id').reset();
      this._noonReportActions.actions.getDetailNoonReport({ report_id: this.detailNoonReport().id });
      this.type.set('view');
    });
  };

  onSave: FunctionType = () => {
    if (this.type() === 'add') {
      const isExisted = some(this.detailNoonReport().supplies, (supply) => supply?.inventory_item_id === this.form.get('inventory_item_id').value);
      if (isExisted) {
        return this._popupService.messageSnackbar({
          type: 'error',
          message: this.translocoService.translate('ship_interface.inventory_already_exists')
        });
      }
      this._noonReportActions.actions.createInventorySupply({ body: this.form.getRawValue() });
      this._noonReportActions.actions.createInventorySupplySuccess$.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
        this._noonReportActions.actions.getDetailNoonReport({ report_id: this.detailNoonReport().id });
        this.form.get('inventory_item_id').reset();
        this.form.get('quantity').reset();
        this.form.get('id').reset();
        this.type.set('view');
      });
    } else if (this.type() === 'edit') {
      const body = cloneDeep(this.form.getRawValue());
      this._noonReportActions.actions.updateInventorySupply({
        id: this.form.get('id').value,
        body
      });
      this._noonReportActions.actions.updateInventorySupplySuccess$.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
        this.form.get('inventory_item_id').reset();
        this.form.get('quantity').reset();
        this.form.get('id').reset();
        this._noonReportActions.actions.getDetailNoonReport({ report_id: this.detailNoonReport().id });
        this.type.set('view');
      });
    } else {
      this.back.emit();
    }
  };

  onAddNew() {
    this.form.get('inventory_item_id').reset();
    this.form.get('quantity').reset();
    this.form.get('id').reset();
    this.type.set('add');
  }

  onChooseSupply(supply: any) {
    this.form.get('inventory_item_id').setValue(supply?.inventory_item_id);
    this.form.get('quantity').setValue(supply?.quantity);
    this.form.get('id').setValue(supply?.id);
    this.type.set('edit');
  }

  onDiscard: FunctionType = () => {
    this.type.set('view');
  };

  onDeleteSupply(supply: any) {
    this._noonReportActions.actions.deleteInventorySupply({ id: supply?.id });
    this._noonReportActions.actions.deleteInventorySupplySuccess$.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
      this.form.get('inventory_item_id').reset();
      this.form.get('quantity').reset();
      this.form.get('id').reset();
      this._noonReportActions.actions.getDetailNoonReport({ report_id: this.detailNoonReport().id });
      this.type.set('view');
    });
  }
}
